import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useElement,
    useListen,
    useProp,
} from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { useStore } from '@atomify/kit';
import { clearFocus } from '@bpd-library/components';
import { ExtendedAreaModels, getApiObjectLayerRaster } from '@bpd-library/utilities';
import {
    getCurrentView,
    setAllProjectsSold,
    updateFilteredData,
    updateHouseSelectorData,
    updateSoldProjectsData,
} from '@features/properties/store';
import useLoadingState from '@source/components/atoms/loading-state/hooks/use-loading-state';
import { apartmentsViewEvents } from '@source/components/molecules/apartments-view/utilities/constants';
import { compareStore, getCompareState } from '@source/store/compare';
import { filterStore } from '@source/store/filters';
import { formatDetailPageUrl } from '@source/utilities/api-formatters';
import getExtendedAreaMapData from '@source/utilities/api-utilities/get-extended-area-map-data';
import { useAreaData, useStylesheet } from '@utilities/hooks';
import { extendApiModelsWithMapData } from '@utilities/map';

import { fetchHouseSelectorData } from './components/house-selector/utilities/house-selector-data';
import { PropertiesViewElement } from './components/properties-view/properties-view';
import { RoutePerObjectHandler, usePropertiesRouting } from './hooks/use-properties-routing';
import style from './properties.scss';
import { getBoundingBox } from './utilities/bounding-box';
import { filterData, filterDataByDistrict, filterHouseSelectorData } from './utilities/data';
import { filterSoldProjectPhasesData } from './utilities/sold-project-phase-data';

const IS_COMPARING_CLASS = 'properties--is-comparing';

interface PropertiesElement extends Component {
    houseSelectorId: string | null;
}

export const Properties: FC<PropertiesElement> = ({ element }) => {
    const [houseSelectorId] = useProp<string | null>('houseSelectorId', null);
    const propertiesFilter = useElement<HTMLDivElement>('[js-hook-properties-filter]');
    const propertiesView = useElement<HTMLDivElement>('[js-hook-properties-view]');

    let propertiesViewElement: PropertiesViewElement | null | undefined = undefined;

    const [, subscribeFilter] = useStore(filterStore);
    const [, subscribeCompare] = useStore(compareStore);
    const { setLoaded } = useLoadingState(element);

    let data: ExtendedAreaModels;
    let filteredData: ExtendedAreaModels;
    let isInitialView = true;

    useStylesheet(style);

    const { setRoute, updateCurrentRoute, setInitialRoute } = usePropertiesRouting();

    onDidLoad(async () => {
        data = await useAreaData();

        data = filterDataByDistrict(data);
        await extendApiModelsWithMapData(data, { alwaysShowSoldProjectPolygons: true });

        const houseSelectorData = houseSelectorId
            ? await fetchHouseSelectorData(houseSelectorId)
            : null;

        // save housing overview data to store before sold data is filtered
        if (houseSelectorData) {
            filterHouseSelectorData(houseSelectorData, data);
            updateHouseSelectorData(houseSelectorData);
        }

        const soldProjectsData = filterSoldProjectPhasesData(data);
        updateSoldProjectsData(soldProjectsData);

        if (!data.projectPhases.length) {
            setAllProjectsSold(true);
            data = soldProjectsData;
        }

        await renderPropertiesView(data, {
            renderHouseSelector: !!houseSelectorData?.views?.length,
        });

        updateFilteredData(data);
        renderPropertiesFilter(data);

        setIsComparingClass();
        setLoaded();
    });

    subscribeFilter(
        ({ filters }) => {
            filteredData = filterData(data, filters);

            updateFilteredData(filteredData);

            if (isInitialView) {
                setInitialRoute();
            } else {
                updateCurrentRoute({ updateHistory: false });
            }

            isInitialView = false;
        },
        ['filters'],
    );

    subscribeCompare(setIsComparingClass);

    useListen(document, apartmentsViewEvents.UPDATE_ALIAS, ({ detail }) => {
        setRoute({
            to: 'apartmentView',
            slug: detail.apartmentBuildingId,
        });
    });

    const handleRouteForObject: RoutePerObjectHandler = (
        object,
        { isFullscreen, viewId, isSoldProjectData },
    ) => {
        const { type: viewType } = getCurrentView() || {};
        const isPropertyTypeRoute =
            viewType === 'propertyType' || viewType === 'apartmentPropertyType';
        const isConstructionNumberRoute =
            viewType === 'constructionNumber' || viewType === 'apartmentConstructionNumber';

        switch (object.objectType) {
            case 'constructionNumber': {
                if (isPropertyTypeRoute) {
                    if (isFullscreen) {
                        propertiesViewElement?.renderCardPopup(object);
                    }

                    if (isSoldProjectData && object.projectPhaseId) {
                        setRoute({ to: 'constructionNumber', slug: object.projectPhaseId, viewId });
                    } else if (object.apartmentBuilding) {
                        setRoute({
                            to: 'apartmentView',
                            slug: object.apartmentBuilding.id,
                            viewId,
                        });
                    } else if (object.propertyTypeId) {
                        setRoute({ to: 'constructionNumber', slug: object.propertyTypeId, viewId });
                    }

                    clearFocus();
                } else if (isConstructionNumberRoute) {
                    if (isFullscreen) {
                        propertiesViewElement?.renderCardPopup(object);
                        if (viewId) updateCurrentRoute({ viewId });
                    } else {
                        const url = formatDetailPageUrl({ constructionNumber: object });
                        if (url) window.location.href = url;
                    }
                } else if (viewId) updateCurrentRoute({ viewId });
                break;
            }
            case 'propertyType': {
                if (isPropertyTypeRoute) {
                    if (object.constructionNumbers?.length) {
                        const slug = isSoldProjectData ? object.projectPhaseId : object.id;
                        slug && setRoute({ to: 'constructionNumber', slug, viewId });
                        clearFocus();
                    } else if (isFullscreen) {
                        propertiesViewElement?.renderCardPopup(object);
                        if (viewId) updateCurrentRoute({ viewId });
                    } else {
                        const url = formatDetailPageUrl({ propertyType: object });
                        if (url) window.location.href = url;
                    }
                    break;
                } else if (viewId) updateCurrentRoute({ viewId });
                break;
            }
            case 'apartmentBuilding': {
                if (isPropertyTypeRoute) {
                    setRoute({ to: 'apartmentView', slug: object.id, viewId });
                } else if (viewId) updateCurrentRoute({ viewId });
                break;
            }
            case 'projectPhase':
            case 'district':
            case 'areaProject': {
                if (
                    isPropertyTypeRoute &&
                    object.objectType === 'projectPhase' &&
                    isSoldProjectData
                ) {
                    setRoute({ to: 'constructionNumber', slug: object.id, viewId });
                    clearFocus();
                } else if (viewId) updateCurrentRoute({ viewId });
                break;
            }
        }
    };

    async function renderPropertiesView(
        data: ExtendedAreaModels,
        options: { renderHouseSelector: boolean },
    ) {
        const layerRaster = getApiObjectLayerRaster(data.areaProject);
        const mapData = getExtendedAreaMapData(data);
        const boundingBox = getBoundingBox(data, mapData);

        propertiesView.current!.append(
            <properties-view
                boundingBox={boundingBox}
                layerRaster={layerRaster}
                hasHouseSelector={options.renderHouseSelector}
                onRouteRequested={handleRouteForObject}
            />,
        );

        propertiesViewElement =
            propertiesView.current?.querySelector<PropertiesViewElement>('properties-view');

        await propertiesViewElement?.componentOnReady();
    }

    function renderPropertiesFilter(data: ExtendedAreaModels) {
        propertiesFilter.current!.append(<properties-filters data={data} />);
    }

    function setIsComparingClass() {
        const { constructionNumberIds } = getCompareState();

        if (constructionNumberIds?.length) {
            element.classList.add(IS_COMPARING_CLASS);
        } else {
            element.classList.remove(IS_COMPARING_CLASS);
        }
    }

    return (
        <Fragment>
            <div class="properties__header">
                <properties-breadcrumbs />
                <div class="properties__filters" js-hook-properties-filter />
            </div>
            <div js-hook-properties-view class="properties__view" />
        </Fragment>
    );
};

Properties.props = {
    houseSelectorId: {
        type: String,
    },
};

defineElement('bpd-properties', Properties);
